import { useQuery } from "@tanstack/react-query";
import { useCsvExport } from "components/CsvExport";
import { useAuthenticatedRequest } from "hooks/useRequests";
import { PullerMetric, ShipperMetric } from "models/shipping";
import { ServerDateFormat } from "models/websiteSettings";
import moment from "moment-timezone";
import { useCallback } from "react";


export interface MetricsRequest {
    start?: string;
    end?: string;
    withinLast?: string;
    userId?: string;
    format?: string;
}

const toUrlSearchParams = (req: MetricsRequest) => {
    const { start, end, withinLast, userId, format } = req;
    const options = new URLSearchParams({});

    if (start && end) {
        options.append("start", start);
        options.append("end", end);
    } else if (withinLast) {
        options.append("withinLast", withinLast);
    }

    if (userId) {
        options.append("userId", userId);
    }

    if (format) {
        options.append("format", format);
    }

    return options;
}

const toExportFilename = ({ start, end, withinLast }: MetricsRequest) => {
    if (start && end) {
        return `${moment(start).format(ServerDateFormat)}_to_${moment(end).format(ServerDateFormat)}`;
    } else if (withinLast) {
        const days = withinLast.split('.')[0];
        return `last-${days}-days`;
    }

    return "export";
}

const useMetrics = <T = PullerMetric | ShipperMetric>(endpoint: string, req: MetricsRequest) => {
    const createConfig = useCallback((csv: boolean = false) => {
        let request = req;
        if (csv === true) {
            request = { ...req, format: "csv" }
        }
        return {
            method: "GET",
            url: `${endpoint}?${toUrlSearchParams(request)}`
        };
    }, [endpoint, req]);

    const key = endpoint.split('/');

    const pullerReq = useAuthenticatedRequest<T[]>(createConfig());

    const csvExport = useCsvExport(createConfig(true), `${key[2]}-${toExportFilename(req)}`);

    return { ...useQuery([...key, req], pullerReq), csvExport };
}

export const usePullerMetrics = (req: MetricsRequest) => {
    return useMetrics<PullerMetric>("shipping/metrics/puller", req);
};

export const useShipperMetrics = (req: MetricsRequest) => {
    return useMetrics<ShipperMetric>("shipping/metrics/shipper", req);
};